import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Spinner, SpinnerSize } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import {
    PeopleRegular,
    PersonAvailableRegular,
    PersonProhibitedRegular,
    CalendarCheckmarkRegular,
    MoneyRegular,
    ArrowTrendingRegular,
    ClockRegular,
    DocumentTextRegular,
} from '@fluentui/react-icons';

import * as CashFlowStatisticsService from '../../../services/CashFlow-Statistics.Services';
import {
    OverviewStatisticViewModel,
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    AgingBucketStatisticViewModel,
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';

import './ContractStatistics.Styles.css';

import { ProtocolConsumedServiceStatusOptions, UnknownConsumedServiceStatusColor } from '../../../models/Entities/Protocols/ProtocolConsumedServiceStatus';

import PatientStatusStatisticsComponent from './PatientStatusStatistics.Component';
import VisitExecutionComponent from './VisitExecution.Component';
import { buildContractVisitExecution, buildEmrVisitExecution } from './VisitExecution.Functions';
import ExcelExportButton from './ExcelExportButton.Component';
import { useEChartsTheme, useStableChartPalette, useThemeMode } from './EChartsTheme';
import { guidePalettes, hexToRgbTriplet, statusPillColors } from '../../../theme/guideTokens';
import {
    buildFileName,
    dateCell,
    downloadWorkbook,
    ExcelRow,
    ExcelSheet,
    indicatorRows,
    money,
    percent,
} from './StatisticsExcel.Functions';

import {
    Props,
    mapStateToProps,
    mapDispatchToProps,
    buildPatientSummaries,
    buildStageSummaries,
    buildConceptSummaries,
    fmt,
    PatientSummary,
    StageSummary,
    ConceptSummary,
} from './ContractStatistics.Functions';

// ─────────────────────────────────────────────────────
// Status display maps
// ─────────────────────────────────────────────────────

/* Los nombres y colores de los estados de consumo salen de
   ProtocolConsumedServiceStatusOptions, que sigue el enum del backend
   (ProtocolConsumedServiceStatus). Acá había una tabla propia —"Borrador",
   "Abierto", "Aprobado"...— que no coincidía con esos estados: el 4, rechazado,
   se mostraba como "Pagado". */

// ─────────────────────────────────────────────────────
// KpiCell — icon + value + label + description + optional sub-label
// ─────────────────────────────────────────────────────

interface KpiCellProps {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    desc: string;
    sub?: string;
    iconColor?: 'blue' | 'green' | 'orange' | 'gray';
}

const KpiCell: React.FC<KpiCellProps> = ({ icon, value, label, desc, sub, iconColor = 'blue' }) => (
    <div className="cs-kpi-cell">
        <div className={`cs-kpi-cell__icon cs-kpi-cell__icon--${iconColor}`}>{icon}</div>
        <div className="cs-kpi-cell__value">{value}</div>
        <div className="cs-kpi-cell__label">{label}</div>
        <div className="cs-kpi-cell__desc">{desc}</div>
        {sub && <div className="cs-kpi-cell__sub">{sub}</div>}
    </div>
);

// ─────────────────────────────────────────────────────
// SectionCard — tarjeta de superficie con encabezado
// ─────────────────────────────────────────────────────

interface SectionCardProps {
    title: string;
    desc?: string;
    children: React.ReactNode;
    noPadding?: boolean;
    /** Acciones de la tarjeta (descarga a Excel), a la derecha del título. */
    actions?: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, desc, children, noPadding, actions }) => (
    <div className="cs-card">
        <div className={'cs-card__header' + (actions ? ' cs-card__header--actions' : '')}>
            <div className="cs-card__header-text">
                <div className="cs-card__title">{title}</div>
                {desc && <div className="cs-card__desc">{desc}</div>}
            </div>
            {actions}
        </div>
        <div className={noPadding ? 'cs-card__chart-body' : 'cs-card__body'}>
            {children}
        </div>
    </div>
);
// ——————————————————————

// ─────────────────────────────────────────────────────
// CashFlowSection — cash flow panel (income/outcome/aging/debt)
// ─────────────────────────────────────────────────────

interface CashFlowSectionProps {
    overview: OverviewStatisticViewModel | null;
    seriesOption: object | null;
    debtOption: object | null;
    agingOption: object | null;
    agingData: AgingBucketStatisticViewModel[];
    currencies: string[];
    activeCurrency: string;
    onCurrencyChange: (c: string) => void;
    isLoading: boolean;
    cfCurrency: string;
    /** Llamadas que fallaron, con el motivo que devolvió la API. */
    errors: string[];
    protocolId: number;
    /** Descarga a Excel de todas las monedas. */
    onExport: () => Promise<void>;
}

const CashFlowSection: React.FC<CashFlowSectionProps> = ({
    overview, seriesOption, debtOption, agingOption, agingData,
    currencies, activeCurrency, onCurrencyChange, isLoading, cfCurrency, errors, protocolId, onExport,
}) => {
    const echartsTheme = useEChartsTheme();
    const noData = !overview && !seriesOption && !debtOption && agingData.length === 0;
    // Todas las llamadas fallaron: no hay nada que mostrar y "sin datos" sería falso.
    const allFailed = errors.length >= 4;
    const showData = !isLoading && !noData;

    return (
        <SectionCard
            title="CashFlow del protocolo"
            desc="Flujo de dinero real registrado en el sistema de cobranzas para este protocolo: ingresos, egresos, evolución de deuda y antigüedad de saldos pendientes."
            noPadding
            actions={<ExcelExportButton onExport={onExport} disabled={isLoading || noData} />}
        >
            {/* Moneda: en la franja de controles, como cualquier otro filtro de tarjeta. */}
            {currencies.length > 1 && (
                <div className="cs-card__toolbar">
                    <div className="cs-cf-currencies">
                        {currencies.map(c => (
                            <button
                                key={c}
                                className={`cs-cf-currency-btn${c === activeCurrency ? ' cs-cf-currency-btn--active' : ''}`}
                                onClick={() => onCurrencyChange(c)}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* KPI de borde a borde, igual que en "Estado operativo" y "Ejecución de visitas". */}
            {showData && overview && (
                <div className="cs-kpi-grid">
                    <KpiCell
                        icon={<ArrowTrendingRegular />}
                        value={fmt(overview.totalPaymentAmount, cfCurrency)}
                        label="Total cobrado"
                        desc="Suma de pagos recibidos para este protocolo"
                        iconColor="green"
                    />
                    <KpiCell
                        icon={<MoneyRegular />}
                        value={fmt(overview.totalDebtOriginalAmount, cfCurrency)}
                        label="Total deuda original"
                        desc="Suma del valor original de las deudas generadas"
                        iconColor="orange"
                    />
                    <KpiCell
                        icon={<ClockRegular />}
                        value={fmt(overview.totalDebtPendingAmount, cfCurrency)}
                        label="Deuda pendiente"
                        desc="Monto aún no cobrado de las deudas registradas"
                        iconColor="orange"
                    />
                    <KpiCell
                        icon={<DocumentTextRegular />}
                        value={fmt(overview.netPosition, cfCurrency)}
                        label="Posición neta"
                        desc="Pagos cobrados menos deuda pendiente. Positivo = superávit"
                        iconColor={overview.netPosition >= 0 ? 'green' : 'orange'}
                        sub={overview.netPosition >= 0 ? 'Superávit' : 'Déficit'}
                    />
                    <KpiCell
                        icon={<PeopleRegular />}
                        value={`${overview.settledDebtCount} / ${overview.debtCount}`}
                        label="Deudas liquidadas"
                        desc="Deudas saldadas sobre el total de deudas registradas"
                        iconColor="blue"
                        sub={overview.debtCount > 0
                            ? `${Math.round((overview.settledDebtCount / overview.debtCount) * 100)}% del total`
                            : undefined}
                    />
                    <KpiCell
                        icon={<ArrowTrendingRegular />}
                        value={fmt(overview.totalPaymentPendingImputationAmount, cfCurrency)}
                        label="Pagos sin imputar"
                        desc="Parte de los pagos recibidos que aún no fue imputada a deudas"
                        iconColor="gray"
                    />
                </div>
            )}

            <div className="cs-card__body">
                {isLoading && (
                    <div className="cs-loading cs-loading--compact">
                        <Spinner size={SpinnerSize.small} />
                        <span>Cargando cashflow…</span>
                    </div>
                )}

                {/* Un error no es lo mismo que "sin datos". Antes cada llamada que fallaba
                    se reemplazaba por una lista vacía, y un 401 o un 500 se veía idéntico a
                    un protocolo sin cashflow. */}
                {!isLoading && errors.length > 0 && (
                    <div className="cs-cf-error" role="alert">
                        <strong>
                            {allFailed
                                ? 'No se pudo obtener el cashflow del protocolo.'
                                : 'Parte del cashflow no se pudo obtener.'}
                        </strong>
                        <ul>
                            {errors.map((error) => <li key={error}>{error}</li>)}
                        </ul>
                    </div>
                )}

                {!isLoading && noData && !allFailed && (
                    <div className="cs-empty cs-empty--compact">
                        <MoneyRegular className="cs-empty__icon" />
                        <span>Sin datos de cashflow para este protocolo.</span>
                        <span className="cs-empty__hint">
                            La API respondió bien pero sin deudas ni pagos asociados al protocolo #{protocolId}.
                            Sólo se cuentan los registrados con este protocolo asignado.
                        </span>
                    </div>
                )}

                {showData && (
                    <>
                        <div className="cs-two-col cs-block">
                            {seriesOption ? (
                                <div>
                                    <div className="cs-cf-chart-title">Flujo mensual de caja (ingresos / egresos)</div>
                                    <div className="cs-cf-chart-desc">Barras: ingresos y egresos por mes. Línea: neto acumulado.</div>
                                    <ReactECharts theme={echartsTheme} option={seriesOption} style={{ height: 300 }} opts={{ renderer: 'svg' }} />
                                </div>
                            ) : (
                                <div className="cs-empty cs-empty--compact">Sin serie de cashflow</div>
                            )}

                            {debtOption ? (
                                <div>
                                    <div className="cs-cf-chart-title">Evolución de deuda por mes</div>
                                    <div className="cs-cf-chart-desc">Nueva deuda vs pagado. Línea: pendiente acumulado.</div>
                                    <ReactECharts theme={echartsTheme} option={debtOption} style={{ height: 300 }} opts={{ renderer: 'svg' }} />
                                </div>
                            ) : (
                                <div className="cs-empty cs-empty--compact">Sin serie de deuda</div>
                            )}
                        </div>

                        {(agingOption || agingData.length > 0) && (
                            <div className="cs-two-col cs-block--divided">
                                {agingOption && (
                                    <div>
                                        <div className="cs-cf-chart-title">Antigüedad de la deuda pendiente</div>
                                        <div className="cs-cf-chart-desc">Distribución del pendiente por bucket de días de mora.</div>
                                        <ReactECharts theme={echartsTheme} option={agingOption} style={{ height: 220 }} opts={{ renderer: 'svg' }} />
                                    </div>
                                )}
                                {agingData.length > 0 && (
                                    <div>
                                        <div className="cs-cf-chart-title">Detalle de aging</div>
                                        <div className="cs-table-wrap">
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>Bucket</th>
                                                        <th className="cs-table__amount">Pendiente</th>
                                                        <th>Deudas</th>
                                                        <th>% del pendiente</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {agingData.map(a => {
                                                        const totalPending = agingData.reduce((sum, x) => sum + x.totalPendingAmount, 0);
                                                        return (
                                                            <tr key={a.bucket}>
                                                                <td><strong>{a.bucket} días</strong></td>
                                                                <td className="cs-table__amount">{fmt(a.totalPendingAmount, cfCurrency)}</td>
                                                                <td>{a.debtCount}</td>
                                                                <td>{totalPending > 0 ? `${Math.round((a.totalPendingAmount / totalPending) * 100)}%` : '—'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </SectionCard>
    );
};

// ─────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────

const ContractStatisticsComponent: React.FC<Props> = (props) => {
    const [t] = useTranslation();
    const chart = useStableChartPalette();
    const echartsTheme = useEChartsTheme();
    const themeMode = useThemeMode();

    const {
        ProtocolId,
        Protocol,
        Contract,
        Stages,
        Consumptions,
        Patients,
        IsLoading,
        AccessToken,
    } = props;

    useEffect(() => {
        if (ProtocolId) {
            props.GetMedicalProtocolById(ProtocolId);
        }
    }, [ProtocolId]);

    useEffect(() => {
        if (!Protocol) return;
        props.GetProtocolPatients(ProtocolId, null);
        props.GetPatientVisitByProtocolId(ProtocolId);
        // Plan de visitas del protocolo: sin contrato es la única referencia de visitas.
        props.GetVisitsByProtocolId(ProtocolId);
        props.GetAllProtocolContractAssociations(ProtocolId);
    }, [Protocol?.id]);

    useEffect(() => {
        if (!Contract) return;
        props.GetAllContractsStage(Contract.id);
        props.GetAllContractsConcept(Contract.id, undefined);
        props.GetAllProtocolConsumptionsByProtocol(ProtocolId, 1, 10000, {} as any);
    }, [Contract?.id]);

    // ── CashFlow statistics (direct service calls, protocol-scoped) ───────
    const [cfOverview, setCfOverview] = useState<OverviewStatisticViewModel[]>([]);
    const [cfSeries, setCfSeries] = useState<CashflowSeriesPointViewModel[]>([]);
    const [cfDebtSeries, setCfDebtSeries] = useState<DebtSeriesPointViewModel[]>([]);
    const [cfAging, setCfAging] = useState<AgingBucketStatisticViewModel[]>([]);
    const [cfLoading, setCfLoading] = useState(false);
    const [cfErrors, setCfErrors] = useState<string[]>([]);
    const [cfCurrency, setCfCurrency] = useState<string>('ARS');

    useEffect(() => {
        if (!AccessToken || !ProtocolId) return;
        let cancelled = false;
        setCfLoading(true);
        setCfErrors([]);
        const filter = { protocolId: ProtocolId };
        const seriesFilter = { protocolId: ProtocolId, granularity: 'Month' as const };

        // Cada llamada sigue sin tumbar a las otras —un aging que falla no tiene por
        // qué esconder el resumen—, pero su fallo queda anotado con el motivo.
        const failures: string[] = [];
        const orEmpty = <T,>(label: string, promise: Promise<T[]>): Promise<T[]> =>
            promise
                .then((result) => result ?? [])
                .catch((error: any) => {
                    const code = error?.ErrorCode ? ` (${error.ErrorCode})` : '';
                    const reason = error?.ErrorMessage || error?.Errors?.[0] || 'error desconocido';
                    failures.push(`${label}${code}: ${reason}`);
                    return [] as T[];
                });

        Promise.all([
            orEmpty<OverviewStatisticViewModel>('Resumen', CashFlowStatisticsService.GetOverview(AccessToken, filter)),
            orEmpty<CashflowSeriesPointViewModel>('Serie de caja', CashFlowStatisticsService.GetCashflowSeries(AccessToken, seriesFilter)),
            orEmpty<DebtSeriesPointViewModel>('Serie de deuda', CashFlowStatisticsService.GetDebtSeries(AccessToken, seriesFilter)),
            orEmpty<AgingBucketStatisticViewModel>('Antigüedad de deuda', CashFlowStatisticsService.GetAging(AccessToken, filter)),
        ]).then(([ov, cs, ds, ag]) => {
            if (cancelled) return;
            setCfOverview(ov);
            setCfSeries(cs);
            setCfDebtSeries(ds);
            setCfAging(ag);
            setCfErrors(failures);
            // Pick first available currency from the data
            const firstCurrency = ov[0]?.currencyCode ?? cs[0]?.currencyCode ?? 'ARS';
            setCfCurrency(firstCurrency);
        }).finally(() => {
            if (!cancelled) setCfLoading(false);
        });
        return () => { cancelled = true; };
    }, [AccessToken, ProtocolId]);

    // ── Derived KPIs ────────────────────────────────────
    const totalPatients = Patients.length;
    const activePatients = Patients.filter(p => p.status === 1).length;
    const inactivePatients = totalPatients - activePatients;

    const uniqueVisitsDone = useMemo(() => {
        const pairs = new Set<string>();
        Consumptions.forEach(c => {
            if (c.stageId && c.patientNumberInProtocol)
                pairs.add(`${c.stageId}::${c.patientNumberInProtocol}`);
        });
        return pairs.size;
    }, [Consumptions]);

    const currency = Consumptions[0]?.currencyCode ?? Contract?.contractCurrency ?? 'ARS';
    const totalAmount = Consumptions.reduce((s, c) => s + (c.totalAmount ?? 0), 0);
    const pendingAmount = Consumptions.reduce((s, c) => s + (c.pendingAmount ?? 0), 0);
    const settledAmount = totalAmount - pendingAmount;
    const withholdingTotal = Consumptions.reduce((s, c) => s + (c.withHolingAmount ?? 0), 0);
    const settledPct = totalAmount > 0 ? Math.round((settledAmount / totalAmount) * 100) : 0;

    // Ejecución de visitas con sus dos fuentes posibles. Con contrato: visitas del
    // contrato y consumos. Sin contrato: plan del protocolo y visitas registradas de
    // cada paciente. Se calculan las dos acá, antes de los retornos tempranos,
    // porque son hooks.
    const contractVisits = useMemo(
        () => buildContractVisitExecution(Stages, Patients, Consumptions),
        [Stages, Patients, Consumptions]
    );
    const emrVisits = useMemo(
        () => buildEmrVisitExecution(props.ProtocolVisits, Patients, props.PatientVisits),
        [props.ProtocolVisits, Patients, props.PatientVisits]
    );
    const visitsProgress = contractVisits.progress;

    // ── Status distribution ─────────────────────────────
    const statusMap = useMemo(() => {
        const m = new Map<number, { count: number; amount: number }>();
        Consumptions.forEach(c => {
            if (!m.has(c.status)) m.set(c.status, { count: 0, amount: 0 });
            const e = m.get(c.status);
            if (!e) return;
            e.count += 1;
            e.amount += c.totalAmount ?? 0;
        });
        return m;
    }, [Consumptions]);

    // ── Summaries ───────────────────────────────────────
    const patientSummaries: PatientSummary[] = useMemo(
        () => buildPatientSummaries(Consumptions, Patients), [Consumptions, Patients]);

    const stageSummaries: StageSummary[] = useMemo(
        () => buildStageSummaries(Consumptions, Stages), [Consumptions, Stages]);

    const conceptSummaries: ConceptSummary[] = useMemo(
        () => buildConceptSummaries(Consumptions), [Consumptions]);

    // ── ECharts: financial donut ─────────────────────────
    // Va sobre el relleno primario del encabezado: los colores se arman con el texto sobre
    // primario de la guía (y los bordes tenues de las pastillas en claro / oscuro).
    const heroDonutColors = useMemo(() => {
        const onPrimary = guidePalettes[themeMode].onPrimary;
        const rgb = hexToRgbTriplet(onPrimary);
        const alpha = (a: number) => `rgba(${rgb}, ${a})`;
        if (themeMode === 'high-contrast') {
            return { settled: onPrimary, pending: alpha(0.55), withholding: alpha(0.25), label: onPrimary, line: alpha(0.6) };
        }
        const pills = statusPillColors[themeMode];
        return {
            settled: themeMode === 'light' ? pills.success.border : pills.success.fg,
            pending: themeMode === 'light' ? pills.warning.border : pills.warning.fg,
            withholding: alpha(0.35),
            label: alpha(0.85),
            line: alpha(0.4),
        };
    }, [themeMode]);

    const donutOption = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            formatter: (p: any) => `${p.name}: ${fmt(p.value, currency)} (${p.percent}%)`,
        },
        series: [{
            type: 'pie',
            radius: ['42%', '68%'],
            center: ['50%', '50%'],
            data: [
                { name: 'Liquidado', value: settledAmount, itemStyle: { color: heroDonutColors.settled } },
                { name: 'Pendiente', value: pendingAmount, itemStyle: { color: heroDonutColors.pending } },
                { name: 'Retención', value: withholdingTotal, itemStyle: { color: heroDonutColors.withholding } },
            ].filter(d => d.value > 0),
            label: {
                color: heroDonutColors.label,
                fontSize: 11,
                formatter: '{b}\n{d}%',
            },
            labelLine: { lineStyle: { color: heroDonutColors.line } },
            emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
        }],
    }), [settledAmount, pendingAmount, withholdingTotal, currency, heroDonutColors]);

    // ── ECharts: status horizontal bar ──────────────────
    const statusBarOption = useMemo(() => {
        const entries = Array.from(statusMap.entries())
            .sort((a, b) => b[1].count - a[1].count);
        const optionFor = (status: number) =>
            ProtocolConsumedServiceStatusOptions.find((option) => option.key === status);
        const labels = entries.map(([s]) => {
            const option = optionFor(s);
            return option
                ? String(t(option.textKey, { defaultValue: option.defaultText }))
                : String(t('protocolConsumedServiceStatus.unknown', { status: s, defaultValue: `Estado ${s}` }));
        });
        const bars = entries.map(([s, d]) => ({
            value: d.count,
            itemStyle: { color: optionFor(s)?.color ?? UnknownConsumedServiceStatusColor, borderRadius: [0, 4, 4, 0] },
        }));
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const p = params[0];
                    const entry = entries[p.dataIndex];
                    return `<b>${p.name}</b><br/>${p.value} consumos<br/>${fmt(entry[1].amount, currency)}`;
                },
            },
            grid: { left: 16, right: 48, top: 12, bottom: 12, containLabel: true },
            xAxis: { type: 'value', axisLabel: { fontSize: 11 }, splitLine: { lineStyle: { color: chart.gridLine } } },
            yAxis: { type: 'category', data: labels, axisLabel: { fontSize: 12 } },
            series: [{
                type: 'bar', data: bars, barMaxWidth: 30,
                label: { show: true, position: 'right', fontSize: 11, color: chart.text, formatter: '{c}' },
            }],
        };
    }, [statusMap, currency, t, chart]);

    // ── ECharts: patient stacked bar ─────────────────────
    const patientChartOption = useMemo(() => {
        const sorted = [...patientSummaries].sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 15);
        const labels = sorted.map(p => `Pac. ${p.numberInProtocol}`);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const s = sorted[params[0].dataIndex];
                    return `<b>Paciente ${s.numberInProtocol}</b><br/>` +
                        `Total: ${fmt(s.totalAmount, currency)}<br/>` +
                        `Liquidado: ${fmt(s.settledAmount, currency)}<br/>` +
                        `Pendiente: ${fmt(s.pendingAmount, currency)}`;
                },
            },
            legend: { data: ['Liquidado', 'Pendiente'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 16, top: 10, bottom: 32, containLabel: true },
            xAxis: { type: 'value', splitLine: { lineStyle: { color: chart.gridLine } }, axisLabel: { fontSize: 10 } },
            yAxis: { type: 'category', data: labels, axisLabel: { fontSize: 11 } },
            series: [
                { name: 'Liquidado', type: 'bar', stack: 'total', barMaxWidth: 26, itemStyle: { color: chart.positive }, data: sorted.map(p => p.settledAmount) },
                { name: 'Pendiente', type: 'bar', stack: 'total', barMaxWidth: 26, itemStyle: { color: chart.warning }, data: sorted.map(p => p.pendingAmount) },
            ],
        };
    }, [patientSummaries, currency, chart]);

    // ── ECharts: stage stacked bar ───────────────────────
    const stageChartOption = useMemo(() => {
        const sorted = [...stageSummaries].sort((a, b) => b.totalAmount - a.totalAmount);
        const labels = sorted.map(s => s.stageName.length > 18 ? `${s.stageName.substring(0, 16)}…` : s.stageName);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const s = sorted[params[0].dataIndex];
                    return `<b>${s.stageName}</b><br/>` +
                        `Total: ${fmt(s.totalAmount, currency)}<br/>` +
                        `Pendiente: ${fmt(s.pendingAmount, currency)}<br/>` +
                        `Pac. realizadas: ${s.visitsDone}`;
                },
            },
            legend: { data: ['Liquidado', 'Pendiente'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 16, top: 10, bottom: 32, containLabel: true },
            xAxis: { type: 'value', splitLine: { lineStyle: { color: chart.gridLine } }, axisLabel: { fontSize: 10 } },
            yAxis: { type: 'category', data: labels, axisLabel: { fontSize: 11 } },
            series: [
                { name: 'Liquidado', type: 'bar', stack: 'total', barMaxWidth: 26, itemStyle: { color: chart.positive }, data: sorted.map(s => s.totalAmount - s.pendingAmount) },
                { name: 'Pendiente', type: 'bar', stack: 'total', barMaxWidth: 26, itemStyle: { color: chart.warning }, data: sorted.map(s => s.pendingAmount) },
            ],
        };
    }, [stageSummaries, currency, chart]);

    // ── ECharts: concept horizontal bar ─────────────────
    const conceptChartOption = useMemo(() => {        const sorted = [...conceptSummaries].sort((a, b) => b.total - a.total).slice(0, 12);
        const labels = sorted.map(c => c.conceptName.length > 22 ? `${c.conceptName.substring(0, 20)}…` : c.conceptName);
        // Dos tonos del primario de la guía, alternados, para separar barras vecinas.
        const palette = [chart.series[0], chart.series[5]];
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const c = sorted[params[0].dataIndex];
                    const pct = totalAmount > 0 ? Math.round((c.total / totalAmount) * 100) : 0;
                    return `<b>${c.conceptName}</b><br/>` +
                        `Total: ${fmt(c.total, currency)}<br/>` +
                        `Liquidado: ${fmt(c.settled, currency)}<br/>` +
                        `${pct}% del total`;
                },
            },
            grid: { left: 8, right: 52, top: 10, bottom: 8, containLabel: true },
            xAxis: { type: 'value', splitLine: { lineStyle: { color: chart.gridLine } }, axisLabel: { fontSize: 10 } },
            yAxis: { type: 'category', data: labels, axisLabel: { fontSize: 11 } },
            series: [{
                type: 'bar', barMaxWidth: 26,
                data: sorted.map((c, i) => ({ value: c.total, itemStyle: { color: palette[i % palette.length], borderRadius: [0, 4, 4, 0] } })),
                label: {
                    show: true, position: 'right', fontSize: 10, color: chart.textSecondary,
                    formatter: (p: any) => totalAmount > 0 ? `${Math.round((p.value / totalAmount) * 100)}%` : '',
                },
            }],
        };
    }, [conceptSummaries, totalAmount, currency, chart]);

    // ── CashFlow derived values ──────────────────────────
    const cfOverviewForCurrency = useMemo(
        () => cfOverview.find(o => o.currencyCode === cfCurrency) ?? cfOverview[0] ?? null,
        [cfOverview, cfCurrency]
    );

    const cfCurrencies = useMemo(() => {
        const s = new Set<string>();
        cfOverview.forEach(o => s.add(o.currencyCode));
        cfSeries.forEach(p => s.add(p.currencyCode));
        return Array.from(s);
    }, [cfOverview, cfSeries]);

    const cfSeriesForCurrency = useMemo(
        () => [...cfSeries.filter(p => p.currencyCode === cfCurrency)]
            .sort((a, b) => a.periodLabel.localeCompare(b.periodLabel)),
        [cfSeries, cfCurrency]
    );

    const cfDebtSeriesForCurrency = useMemo(
        () => [...cfDebtSeries.filter(p => p.currencyCode === cfCurrency)]
            .sort((a, b) => a.periodLabel.localeCompare(b.periodLabel)),
        [cfDebtSeries, cfCurrency]
    );

    const cfAgingForCurrency = useMemo(
        () => cfAging.filter(a => a.currencyCode === cfCurrency),
        [cfAging, cfCurrency]
    );

    // ── ECharts: cashflow income/outcome bars + cumulative net line ───────
    const cfSeriesChartOption = useMemo(() => {
        if (cfSeriesForCurrency.length === 0) return null;
        const labels = cfSeriesForCurrency.map(p => p.periodLabel);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'cross' },
                formatter: (params: any[]) => {
                    const idx = params[0]?.dataIndex ?? 0;
                    const p = cfSeriesForCurrency[idx];
                    return `<b>${p.periodLabel}</b><br/>` +
                        `Ingresos: ${fmt(p.income, cfCurrency)}<br/>` +
                        `Egresos: ${fmt(p.outcome, cfCurrency)}<br/>` +
                        `Neto: ${fmt(p.net, cfCurrency)}<br/>` +
                        `Neto acum.: ${fmt(p.cumulativeNet, cfCurrency)}`;
                },
            },
            legend: { data: ['Ingresos', 'Egresos', 'Neto acumulado'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 8, top: 14, bottom: 36, containLabel: true },
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, rotate: 30 } },
            yAxis: [
                { type: 'value', splitLine: { lineStyle: { color: chart.gridLine } }, axisLabel: { fontSize: 10 } },
                { type: 'value', splitLine: { show: false }, axisLabel: { fontSize: 10 } },
            ],
            series: [
                { name: 'Ingresos', type: 'bar', barMaxWidth: 28, itemStyle: { color: chart.positive }, data: cfSeriesForCurrency.map(p => p.income) },
                { name: 'Egresos', type: 'bar', barMaxWidth: 28, itemStyle: { color: chart.negative }, data: cfSeriesForCurrency.map(p => p.outcome) },
                {
                    name: 'Neto acumulado', type: 'line', yAxisIndex: 1,
                    smooth: true, symbol: 'circle', symbolSize: 5,
                    lineStyle: { color: chart.series[3], width: 2 },
                    itemStyle: { color: chart.series[3] },
                    areaStyle: { color: chart.series[3], opacity: 0.07 },
                    data: cfSeriesForCurrency.map(p => p.cumulativeNet),
                },
            ],
        };
    }, [cfSeriesForCurrency, cfCurrency, chart]);

    // ── ECharts: debt evolution ───────────────────────────
    const cfDebtChartOption = useMemo(() => {
        if (cfDebtSeriesForCurrency.length === 0) return null;
        const labels = cfDebtSeriesForCurrency.map(p => p.periodLabel);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'cross' },
                formatter: (params: any[]) => {
                    const idx = params[0]?.dataIndex ?? 0;
                    const p = cfDebtSeriesForCurrency[idx];
                    return `<b>${p.periodLabel}</b><br/>` +
                        `Nueva deuda: ${fmt(p.newDebtAmount, cfCurrency)}<br/>` +
                        `Pagado: ${fmt(p.paidAmount, cfCurrency)}<br/>` +
                        `Pendiente: ${fmt(p.pendingAmount, cfCurrency)}<br/>` +
                        `Tasa liquidación: ${p.settlementRate.toFixed(1)}%`;
                },
            },
            legend: { data: ['Nueva deuda', 'Pagado', 'Pendiente acum.'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 8, top: 14, bottom: 36, containLabel: true },
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, rotate: 30 } },
            yAxis: [
                { type: 'value', splitLine: { lineStyle: { color: chart.gridLine } }, axisLabel: { fontSize: 10 } },
                { type: 'value', splitLine: { show: false }, axisLabel: { fontSize: 10 } },
            ],
            series: [
                { name: 'Nueva deuda', type: 'bar', barMaxWidth: 28, itemStyle: { color: chart.negative }, data: cfDebtSeriesForCurrency.map(p => p.newDebtAmount) },
                { name: 'Pagado', type: 'bar', barMaxWidth: 28, itemStyle: { color: chart.positive }, data: cfDebtSeriesForCurrency.map(p => p.paidAmount) },
                {
                    name: 'Pendiente acum.', type: 'line', yAxisIndex: 1,
                    smooth: true, symbol: 'circle', symbolSize: 5,
                    lineStyle: { color: chart.series[4], width: 2 },
                    itemStyle: { color: chart.series[4] },
                    areaStyle: { color: chart.series[4], opacity: 0.06 },
                    data: cfDebtSeriesForCurrency.map(p => p.cumulativePendingAmount),
                },
            ],
        };
    }, [cfDebtSeriesForCurrency, cfCurrency, chart]);

    // ── ECharts: aging donut ──────────────────────────────
    const cfAgingChartOption = useMemo(() => {
        if (cfAgingForCurrency.length === 0) return null;
        const agingColors: Record<string, string> = {
            '0-30': chart.series[2],
            '31-60': chart.warning,
            '61-90': chart.series[4],
            '90+': themeMode === 'light' ? chart.series[7] : chart.confirmed,
        };
        return {
            tooltip: {
                trigger: 'item',
                formatter: (p: any) => `<b>${p.name}</b><br/>${fmt(p.value, cfCurrency)}<br/>${p.data.debtCount} deudas (${p.percent}%)`,
            },
            series: [{
                type: 'pie', radius: ['38%', '65%'],
                data: cfAgingForCurrency.map(a => ({
                    name: `${a.bucket} días`,
                    value: a.totalPendingAmount,
                    debtCount: a.debtCount,
                    itemStyle: { color: agingColors[a.bucket] ?? chart.textSecondary },
                })),
                label: { fontSize: 11, formatter: '{b}\n{d}%' },
                emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
            }],
        };
    }, [cfAgingForCurrency, cfCurrency, chart, themeMode]);

    // ── Descargas a Excel ───────────────────────────────
    // Cada sección baja todo su detalle, no sólo lo que entra en el gráfico.
    const fileBase = `protocolo-${Protocol?.protocolNumber || ProtocolId}`;
    const exportScope = `Protocolo ${Protocol?.protocolNumber || ProtocolId}${Protocol?.title ? ` · ${Protocol.title}` : ''}`;

    /** Un libro por sección, con el mismo encabezado en todas las hojas. */
    const exportBook = (slug: string, title: string, sheets: ExcelSheet[], note?: string) =>
        downloadWorkbook(buildFileName(`${fileBase}-${slug}`), sheets, { title, scope: exportScope, note });

    const consumptionStatusLabel = (status: number): string => {
        const option = ProtocolConsumedServiceStatusOptions.find((item) => item.key === status);
        return option
            ? String(t(option.textKey, { defaultValue: option.defaultText }))
            : String(t('protocolConsumedServiceStatus.unknown', { status, defaultValue: `Estado ${status}` }));
    };

    const exportOperational = () =>
        exportBook('estado-operativo', 'Estado operativo', [{
            name: 'Estado operativo',
            description: 'Participación de pacientes, avance de visitas y montos del contrato.',
            note: `Importes en ${currency}.`,
            rows: indicatorRows([
                ['Pacientes totales', totalPatients, 'Total de pacientes enrolados en el protocolo'],
                ['Pacientes activos', activePatients],
                ['Pacientes inactivos', inactivePatients],
                ['Visitas por paciente', Stages.length, 'Plan de visitas del contrato'],
                ['Visitas esperadas', visitsProgress.expected, 'Visitas obligatorias × pacientes randomizados'],
                ['Visitas esperadas realizadas', visitsProgress.doneExpected],
                ['% de avance', percent(visitsProgress.percentage)],
                ['Consumos totales', Consumptions.length],
                ['Moneda', currency],
                ['Monto total', money(totalAmount)],
                ['Liquidado', money(settledAmount)],
                ['Pendiente', money(pendingAmount)],
                ['Retenciones', money(withholdingTotal)],
                ['Prom. por paciente', totalPatients > 0 ? money(totalAmount / totalPatients) : ''],
                ['Prom. por visita', uniqueVisitsDone > 0 ? money(totalAmount / uniqueVisitsDone) : ''],
            ]),
        }]);

    const exportConsumptionStatus = () =>
        exportBook('estado-de-consumos', 'Distribución por estado de consumos', [
            {
                name: 'Por estado',
                description: 'Consumos y monto agrupados por estado de gestión.',
                note: `Importes en ${currency}.`,
                rows: Array.from(statusMap.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([status, data]) => ({
                        Estado: consumptionStatusLabel(status),
                        Consumos: data.count,
                        Monto: money(data.amount),
                        '% de los consumos': percent(Consumptions.length > 0
                            ? Math.round((data.count / Consumptions.length) * 1000) / 10
                            : 0),
                    })),
            },
            {
                name: 'Consumos',
                description: 'Todos los consumos registrados en el protocolo.',
                rows: Consumptions.map((c): ExcelRow => ({
                    Paciente: c.patientNumberInProtocol ?? '',
                    Visita: c.stageName ?? '',
                    Servicio: c.serviceName ?? '',
                    Prestación: c.medicalServiceName ?? '',
                    Concepto: c.conceptDescription ?? '',
                    Fecha: dateCell(c.serviceDueDate),
                    Estado: consumptionStatusLabel(c.status),
                    Moneda: c.currencyCode ?? '',
                    Cantidad: c.quantity,
                    Total: money(c.totalAmount ?? 0),
                    Pagado: money(c.payeeAmount ?? 0),
                    Pendiente: money(c.pendingAmount ?? 0),
                    Retención: money(c.withHolingAmount ?? 0),
                    Liquidación: c.settlementName ?? '',
                    Prestador: c.providerName ?? '',
                    Efector: c.producerName ?? '',
                })),
            },
        ]);

    const exportPatients = () =>
        exportBook('monto-por-paciente', 'Monto por paciente', [{
            name: 'Por paciente',
            description: 'Monto liquidado y pendiente de cada paciente enrolado.',
            rows: patientSummaries.map((p) => ({
                'N° en protocolo': p.numberInProtocol,
                Moneda: p.currency,
                Total: money(p.totalAmount),
                Liquidado: money(p.settledAmount),
                Pendiente: money(p.pendingAmount),
                Consumos: p.consumptionCount,
                'Visitas realizadas': p.visitsDone,
            })),
        }]);

    const exportStages = () =>
        exportBook('monto-por-visita', 'Monto por visita / etapa', [{
            name: 'Por visita',
            description: 'Monto liquidado y pendiente de cada etapa del protocolo.',
            rows: stageSummaries.map((s) => ({
                Visita: s.stageName,
                Moneda: s.currency,
                Total: money(s.totalAmount),
                Pendiente: money(s.pendingAmount),
                'Pacientes con la visita realizada': s.visitsDone,
            })),
        }]);

    // Todos los conceptos: el gráfico muestra los 12 mayores, el Excel no corta.
    const exportConcepts = () =>
        exportBook('desglose-por-concepto', 'Desglose por concepto', [{
            name: 'Por concepto',
            description: 'Todos los conceptos facturados, de mayor a menor monto.',
            note: `Importes en ${currency}. El gráfico de la pantalla muestra los 12 mayores; esta hoja, todos.`,
            rows: [...conceptSummaries]
                .sort((a, b) => b.total - a.total)
                .map((c) => ({
                    Concepto: c.conceptName,
                    Moneda: c.currency,
                    Total: money(c.total),
                    Liquidado: money(c.settled),
                    Pendiente: money(c.pending),
                    '% del total': totalAmount > 0 ? percent(Math.round((c.total / totalAmount) * 1000) / 10) : '',
                })),
        }]);

    // CashFlow de todas las monedas, no sólo la seleccionada.
    const exportCashFlow = () =>
        exportBook(
            'cashflow',
            'CashFlow del protocolo',
            [
                {
                    name: 'Resumen',
                    description: 'Cobrado, deuda y posición neta, una fila por moneda.',
                    rows: cfOverview.map((o) => ({
                        Moneda: o.currencyCode,
                        'Total cobrado': money(o.totalPaymentAmount),
                        'Pagos imputados': money(o.totalPaymentImputedAmount),
                        'Pagos sin imputar': money(o.totalPaymentPendingImputationAmount),
                        Pagos: o.paymentCount,
                        'Deuda original': money(o.totalDebtOriginalAmount),
                        'Deuda pagada': money(o.totalDebtPaidAmount),
                        'Deuda pendiente': money(o.totalDebtPendingAmount),
                        Deudas: o.debtCount,
                        'Deudas liquidadas': o.settledDebtCount,
                        'Deudas pendientes': o.pendingDebtCount,
                        'Posición neta': money(o.netPosition),
                    })),
                },
                {
                    name: 'Flujo mensual',
                    description: 'Ingresos y egresos por período, con el neto acumulado.',
                    rows: cfSeries.map((p) => ({
                        Moneda: p.currencyCode,
                        Período: p.periodLabel,
                        Desde: dateCell(p.periodStart),
                        Hasta: dateCell(p.periodEnd),
                        Ingresos: money(p.income),
                        'Cant. ingresos': p.incomeCount,
                        Egresos: money(p.outcome),
                        'Cant. egresos': p.outcomeCount,
                        Neto: money(p.net),
                        'Neto acumulado': money(p.cumulativeNet),
                    })),
                },
                {
                    name: 'Evolución de deuda',
                    description: 'Nueva deuda, pagos y pendiente acumulado por período.',
                    rows: cfDebtSeries.map((p) => ({
                        Moneda: p.currencyCode,
                        Período: p.periodLabel,
                        Desde: dateCell(p.periodStart),
                        Hasta: dateCell(p.periodEnd),
                        'Nueva deuda': money(p.newDebtAmount),
                        'Cant. nuevas deudas': p.newDebtCount,
                        Pagado: money(p.paidAmount),
                        Pendiente: money(p.pendingAmount),
                        Liquidadas: p.settledCount,
                        'Tasa de liquidación': percent(p.settlementRate),
                        'Pendiente acumulado': money(p.cumulativePendingAmount),
                    })),
                },
                {
                    name: 'Antigüedad de deuda',
                    description: 'Deuda pendiente por tramo de días de mora.',
                    rows: cfAging.map((a) => ({
                        Moneda: a.currencyCode,
                        Tramo: `${a.bucket} días`,
                        'Desde (días)': a.fromDays,
                        'Hasta (días)': a.toDays ?? '',
                        Pendiente: money(a.totalPendingAmount),
                        Deudas: a.debtCount,
                    })),
                },
            ],
            'Sólo se cuentan las deudas y los pagos que tienen este protocolo asignado.'
        );

    // ── Guard renders ────────────────────────────────────
    if (IsLoading && Consumptions.length === 0) {
        return (
            <div className="cs-loading">
                <Spinner size={SpinnerSize.medium} />
                <span>Cargando estadísticas…</span>
            </div>
        );
    }

    if (!Contract) {
        // Sin contrato no hay visitas del contrato ni consumos, pero el protocolo sí
        // tiene su plan de visitas y las visitas registradas de cada paciente: la
        // ejecución de visitas se arma con eso. El estado de los pacientes tampoco
        // depende del contrato.
        return (
            <div className="cs-container">
                <div className="cs-content">
                    <div className="cs-banner" role="status">
                        <DocumentTextRegular className="cs-banner__icon" />
                        <span>
                            <strong>Este protocolo no tiene un contrato asociado.</strong> Se muestran los pacientes y
                            las visitas según el registro clínico. Los montos, los consumos y el cashflow aparecen
                            cuando el protocolo tiene un contrato.
                        </span>
                    </div>
                    <PatientStatusStatisticsComponent
                        ProtocolId={ProtocolId}
                        AccessToken={AccessToken}
                        ExportScope={exportScope}
                    />
                    <VisitExecutionComponent
                        model={emrVisits}
                        source="emr"
                        isLoading={!!Protocol?.isGettingVisits}
                        fileBase={`${fileBase}-ejecucion-de-visitas`}
                        exportScope={exportScope}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="cs-container">

            {/* ── Hero band ── */}
            <div className="cs-hero">
                <div className="cs-hero__header">
                    <div className="cs-hero__title">Tablero financiero y operativo</div>
                    <div className="cs-hero__subtitle">
                        Contrato <strong>{Contract.contractNumber}</strong>
                        {Contract.contractDescription ? ` — ${Contract.contractDescription}` : ''}
                        {Contract.contractCurrency ? ` · ${Contract.contractCurrency}` : ''}
                    </div>
                </div>

                <div className="cs-hero__body">
                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Monto total</div>
                        <div className="cs-hero__kpi-value">{fmt(totalAmount, currency)}</div>
                        <div className="cs-hero__kpi-desc">Suma de todos los consumos registrados</div>
                    </div>
                    <div className="cs-hero__kpi cs-hero__kpi--liquidado">
                        <div className="cs-hero__kpi-label">Liquidado</div>
                        <div className="cs-hero__kpi-value">{fmt(settledAmount, currency)}</div>
                        <div className="cs-hero__kpi-sub">{settledPct}% del total</div>
                        <div className="cs-hero__kpi-desc">Acreditado o en proceso de pago</div>
                    </div>
                    <div className="cs-hero__kpi cs-hero__kpi--pendiente">
                        <div className="cs-hero__kpi-label">Pendiente</div>
                        <div className="cs-hero__kpi-value">{fmt(pendingAmount, currency)}</div>
                        <div className="cs-hero__kpi-desc">Aún no liquidado o en revisión</div>
                    </div>
                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Retenciones</div>
                        <div className="cs-hero__kpi-value">{fmt(withholdingTotal, currency)}</div>
                        <div className="cs-hero__kpi-desc">Retenciones impositivas aplicadas</div>
                    </div>
                    {totalAmount > 0 && (
                        <div className="cs-hero__donut">
                            <ReactECharts
                                theme={echartsTheme}
                                option={donutOption}
                                style={{ height: 185, width: 185 }}
                                opts={{ renderer: 'svg' }}
                            />
                        </div>
                    )}
                </div>

                {totalAmount > 0 && (
                    <div className="cs-hero__progress">
                        <div className="cs-hero__progress-bar">
                            <div className="cs-hero__progress-fill" style={{ width: `${settledPct}%` }} />
                        </div>
                        <div className="cs-hero__progress-label">{settledPct}% del monto total liquidado</div>
                    </div>
                )}
            </div>

            <div className="cs-content">

                {/* ── KPIs operativos ── */}
                <SectionCard
                    title="Estado operativo"
                    desc="Resumen de la participación de pacientes y avance de visitas en el protocolo."
                    noPadding
                    actions={<ExcelExportButton onExport={exportOperational} />}
                >
                    <div className="cs-kpi-grid">
                        <KpiCell
                            icon={<PeopleRegular />}
                            value={totalPatients}
                            label="Pacientes totales"
                            desc="Total de pacientes enrolados en el protocolo"
                            iconColor="blue"
                        />
                        <KpiCell
                            icon={<PersonAvailableRegular />}
                            value={activePatients}
                            label="Pacientes activos"
                            desc="Pacientes que continúan en seguimiento activo"
                            iconColor="green"
                        />
                        <KpiCell
                            icon={<PersonProhibitedRegular />}
                            value={inactivePatients}
                            label="Pacientes inactivos"
                            desc="Dados de baja, discontinuados o finalizados"
                            iconColor="gray"
                        />
                        <KpiCell
                            icon={<CalendarCheckmarkRegular />}
                            value={Stages.length}
                            label="Visitas por paciente"
                            desc="Plan de visitas del contrato, para cada paciente"
                            iconColor="blue"
                        />
                        <KpiCell
                            icon={<ClockRegular />}
                            value={`${visitsProgress.doneExpected} de ${visitsProgress.expected}`}
                            label="Visitas esperadas realizadas"
                            desc="Visitas obligatorias de pacientes randomizados con consumos cargados"
                            sub={visitsProgress.expected > 0 ? `${visitsProgress.percentage}% de avance` : undefined}
                            iconColor="green"
                        />
                        <KpiCell
                            icon={<DocumentTextRegular />}
                            value={Consumptions.length}
                            label="Consumos totales"
                            desc="Ítems de consumo registrados en el protocolo"
                            iconColor="blue"
                        />
                        <KpiCell
                            icon={<MoneyRegular />}
                            value={totalPatients > 0 ? fmt(totalAmount / totalPatients, currency) : '—'}
                            label="Prom. por paciente"
                            desc="Monto total dividido entre pacientes enrolados"
                            iconColor="blue"
                        />
                        <KpiCell
                            icon={<ArrowTrendingRegular />}
                            value={uniqueVisitsDone > 0 ? fmt(totalAmount / uniqueVisitsDone, currency) : '—'}
                            label="Prom. por visita"
                            desc="Monto promedio por cada visita realizada"
                            iconColor="blue"
                        />
                    </div>
                </SectionCard>

                {/* ── Estado de los pacientes ── */}
                <PatientStatusStatisticsComponent
                    ProtocolId={ProtocolId}
                    AccessToken={AccessToken}
                    ExportScope={exportScope}
                />

                {/* ── Ejecución de visitas ── */}
                <VisitExecutionComponent
                    model={contractVisits}
                    source="contract"
                    fileBase={`${fileBase}-ejecucion-de-visitas`}
                    exportScope={exportScope}
                />

                {/* ── Estado de consumos ── */}
                {statusMap.size > 0 && (
                    <SectionCard
                        title="Distribución por estado de consumos"
                        desc="Cantidad de consumos agrupados por estado de gestión. Permite identificar cuellos de botella en el flujo de liquidación."
                        noPadding
                        actions={<ExcelExportButton onExport={exportConsumptionStatus} />}
                    >
                        <ReactECharts
                            theme={echartsTheme}
                            option={statusBarOption}
                            style={{ height: Math.max(180, statusMap.size * 46) }}
                            opts={{ renderer: 'svg' }}
                        />
                    </SectionCard>
                )}

                {/* ── Por paciente / por visita ── */}
                <div className="cs-two-col">

                    <SectionCard
                        title="Monto por paciente"
                        desc="Distribución del monto liquidado y pendiente para cada paciente enrolado."
                        actions={<ExcelExportButton onExport={exportPatients} disabled={patientSummaries.length === 0} />}
                    >
                        {patientSummaries.length > 0 ? (
                            <>
                                <ReactECharts
                                    theme={echartsTheme}
                                    option={patientChartOption}
                                    style={{ height: Math.max(190, Math.min(patientSummaries.length * 38, 370)) }}
                                    opts={{ renderer: 'svg' }}
                                />
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>Nº</th>
                                                <th className="cs-table__amount">Total</th>
                                                <th className="cs-table__amount">Liquidado</th>
                                                <th className="cs-table__amount">Pendiente</th>
                                                <th>Consumos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {patientSummaries.map(p => (
                                                <tr key={p.numberInProtocol}>
                                                    <td><strong>{p.numberInProtocol}</strong></td>
                                                    <td className="cs-table__amount">{fmt(p.totalAmount, currency)}</td>
                                                    <td className="cs-table__amount">{fmt(p.settledAmount, currency)}</td>
                                                    <td className="cs-table__amount">{fmt(p.pendingAmount, currency)}</td>
                                                    <td>{p.consumptionCount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="cs-empty cs-empty--compact">Sin datos de pacientes</div>
                        )}
                    </SectionCard>

                    <SectionCard
                        title="Monto por visita / etapa"
                        desc="Distribución del monto liquidado y pendiente para cada etapa del protocolo."
                        actions={<ExcelExportButton onExport={exportStages} disabled={stageSummaries.length === 0} />}
                    >
                        {stageSummaries.length > 0 ? (
                            <>
                                <ReactECharts
                                    theme={echartsTheme}
                                    option={stageChartOption}
                                    style={{ height: Math.max(190, Math.min(stageSummaries.length * 38, 370)) }}
                                    opts={{ renderer: 'svg' }}
                                />
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>Visita</th>
                                                <th className="cs-table__amount">Total</th>
                                                <th className="cs-table__amount">Pendiente</th>
                                                <th>Pac. realizadas</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stageSummaries.map(s => (
                                                <tr key={s.stageId}>
                                                    <td>{s.stageName}</td>
                                                    <td className="cs-table__amount">{fmt(s.totalAmount, currency)}</td>
                                                    <td className="cs-table__amount">{fmt(s.pendingAmount, currency)}</td>
                                                    <td>{s.visitsDone}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="cs-empty cs-empty--compact">Sin datos de visitas</div>
                        )}
                    </SectionCard>
                </div>

                {/* ── Por concepto ── */}
                {conceptSummaries.length > 0 && (
                    <SectionCard
                        title="Desglose por concepto (top 12)"
                        desc="Distribución del monto total por tipo de concepto facturado. Permite identificar los conceptos de mayor impacto económico en el protocolo."
                        actions={<ExcelExportButton onExport={exportConcepts} />}
                    >
                        <ReactECharts
                            theme={echartsTheme}
                            option={conceptChartOption}
                            style={{ height: Math.max(220, Math.min(conceptSummaries.length * 36, 450)) }}
                            opts={{ renderer: 'svg' }}
                        />
                        <div className="cs-table-wrap">
                            <table className="cs-table">
                                <thead>
                                    <tr>
                                        <th>Concepto</th>
                                        <th className="cs-table__amount">Total</th>
                                        <th className="cs-table__amount">Liquidado</th>
                                        <th className="cs-table__amount">Pendiente</th>
                                        <th>% del total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {conceptSummaries.map(c => (
                                        <tr key={c.conceptName}>
                                            <td>{c.conceptName}</td>
                                            <td className="cs-table__amount">{fmt(c.total, currency)}</td>
                                            <td className="cs-table__amount">{fmt(c.settled, currency)}</td>
                                            <td className="cs-table__amount">{fmt(c.pending, currency)}</td>
                                            <td>
                                                {totalAmount > 0
                                                    ? `${Math.round((c.total / totalAmount) * 100)}%`
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SectionCard>
                )}

                {/* ── CashFlow section ── */}
                <CashFlowSection
                    overview={cfOverviewForCurrency}
                    seriesOption={cfSeriesChartOption}
                    debtOption={cfDebtChartOption}
                    agingOption={cfAgingChartOption}
                    agingData={cfAgingForCurrency}
                    currencies={cfCurrencies}
                    activeCurrency={cfCurrency}
                    onCurrencyChange={setCfCurrency}
                    isLoading={cfLoading}
                    cfCurrency={cfCurrency}
                    errors={cfErrors}
                    protocolId={ProtocolId}
                    onExport={exportCashFlow}
                />

            </div>
        </div>
    );
};

export const ContractStatistics = connect(
    mapStateToProps,
    mapDispatchToProps
)(ContractStatisticsComponent as any);

export default ContractStatistics;
